import React, { useEffect, useState } from 'react';
import { 
  Store, 
  Filter, 
  Download, 
  RefreshCw,
  Search,
  ChevronDown,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  Shield,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import BusinessTable from '../../components/admin/marketplace/BusinessTable';
import useAdminStore from '../../store/adminStore';
import { toast } from 'react-hot-toast';

const AdminBusinessesPage = () => {
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    status: '',
    category: '',
    tier: '',
    verified: '',
    featured: '',
    search: '',
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [businessToVerify, setBusinessToVerify] = useState(null);
  const [verifyAction, setVerifyAction] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const {
    marketplaceBusinesses,
    marketplaceBusinessesPagination,
    isLoading,
    getAllBusinesses,
    getBusinessDetailsAdmin,
    updateBusinessAdmin,
    verifyBusiness,
    selectedBusiness,
    setSelectedBusiness,
  } = useAdminStore();

  useEffect(() => {
    loadBusinesses();
  }, [filters.page, filters.limit, filters.status, filters.category, filters.tier, filters.verified]);

  const loadBusinesses = async () => {
    try {
      await getAllBusinesses(filters);
    } catch (error) {
      console.error('Failed to load businesses:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, page: 1 });
    loadBusinesses();
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value, page: 1 });
  };

  const handleRefresh = () => {
    loadBusinesses();
    toast.success('Businesses refreshed');
  };

  const handleExport = () => {
    toast.success('Export started');
  };

  const handleViewBusiness = async (business) => {
    try {
      const details = await getBusinessDetailsAdmin(business.id);
      setSelectedBusiness(details);
      toast.success('Business details loaded');
    } catch (error) {
      console.error('Failed to load business details:', error);
    }
  };

  const handleEditBusiness = (business) => {
    setSelectedBusiness(business);
    setShowEditModal(true);
  };

  const handleVerifyBusiness = (business, verified) => {
    setBusinessToVerify(business);
    setVerifyAction(verified);
    setShowVerifyModal(true);
  };

  const confirmVerifyBusiness = async () => {
    try {
      await verifyBusiness(businessToVerify.id, verifyAction);
      setShowVerifyModal(false);
      setBusinessToVerify(null);
      loadBusinesses();
      toast.success(`Business ${verifyAction ? 'verified' : 'unverified'} successfully`);
    } catch (error) {
      toast.error(error.message || 'Failed to update verification status');
    }
  };

  const handleUpdateBusiness = async (businessId, data) => {
    try {
      await updateBusinessAdmin(businessId, data);
      setShowEditModal(false);
      loadBusinesses();
      toast.success('Business updated successfully');
    } catch (error) {
      console.error('Failed to update business:', error);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (marketplaceBusinessesPagination?.totalPages || 1)) {
      setFilters({ ...filters, page: newPage });
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setFilters({ ...filters, limit: newLimit, page: 1 });
  };

  const getStatusCounts = () => {
    const counts = {
      ACTIVE: 0,
      PENDING: 0,
      SUSPENDED: 0,
      DRAFT: 0,
      TOTAL: marketplaceBusinesses?.length || 0
    };

    marketplaceBusinesses?.forEach(business => {
      if (counts[business.status] !== undefined) {
        counts[business.status]++;
      }
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const total = marketplaceBusinessesPagination?.total || 0;
  const currentPage = marketplaceBusinessesPagination?.page || 1;
  const totalPages = marketplaceBusinessesPagination?.totalPages || 1;
  const limit = marketplaceBusinessesPagination?.limit || 20;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#E8ECF5] rounded-xl p-3 shadow-neomorph mb-3 border border-primary-100/50">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <div className="flex items-center space-x-1.5 mb-0.5">
                <div className="w-1 h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
                <h1 className="text-lg font-bold text-gray-800">Business Management</h1>
              </div>
              <p className="text-xs text-gray-500 ml-2.5">Manage all marketplace businesses</p>
            </div>
            
            <div className="mt-2 md:mt-0 flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                Filters
                <ChevronDown className={`h-3.5 w-3.5 ml-1.5 transform transition-transform ${
                  showFilters ? 'rotate-180' : ''
                }`} />
              </button>
              <button
                onClick={handleExport}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Export
              </button>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Total Businesses</p>
                <p className="text-base font-bold text-gray-800">{formatNumber(statusCounts.TOTAL)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-primary-500 to-primary-600 rounded-md shadow-md">
                <Store className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Active</p>
                <p className="text-base font-bold text-green-600">{formatNumber(statusCounts.ACTIVE)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-green-500 to-green-600 rounded-md shadow-md">
                <CheckCircle className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Pending</p>
                <p className="text-base font-bold text-yellow-600">{formatNumber(statusCounts.PENDING)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-md shadow-md">
                <Clock className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Suspended</p>
                <p className="text-base font-bold text-red-600">{formatNumber(statusCounts.SUSPENDED)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-red-500 to-red-600 rounded-md shadow-md">
                <AlertTriangle className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 shadow-neomorph border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] text-gray-600 mb-0.5">Draft</p>
                <p className="text-base font-bold text-gray-600">{formatNumber(statusCounts.DRAFT)}</p>
              </div>
              <div className="p-1.5 bg-gradient-to-br from-gray-500 to-gray-600 rounded-md shadow-md">
                <XCircle className="h-3.5 w-3.5 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100 mb-3">
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Search</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      placeholder="Search..."
                      className="block w-full pl-8 pr-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    />
                    <Search className="absolute left-2.5 top-1.5 h-3.5 w-3.5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Status</label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">All</option>
                    <option value="ACTIVE">Active</option>
                    <option value="PENDING">Pending</option>
                    <option value="DRAFT">Draft</option>
                    <option value="SUSPENDED">Suspended</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">All</option>
                    <option value="TECHNOLOGY">Technology</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="DESIGN">Design</option>
                    <option value="TELECOM">Telecom</option>
                    <option value="CONSULTING">Consulting</option>
                    <option value="CONTENT">Content</option>
                    <option value="SERVICES">Services</option>
                    <option value="PRODUCTS">Products</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Tier</label>
                  <select
                    value={filters.tier}
                    onChange={(e) => handleFilterChange('tier', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">All</option>
                    <option value="BASIC">Basic</option>
                    <option value="FEATURED">Featured</option>
                    <option value="PREMIUM">Premium</option>
                    <option value="ENTERPRISE">Enterprise</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-600 mb-1">Verified</label>
                  <select
                    value={filters.verified}
                    onChange={(e) => handleFilterChange('verified', e.target.value)}
                    className="block w-full px-2 py-1.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="">All</option>
                    <option value="true">Verified</option>
                    <option value="false">Unverified</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setFilters({
                      page: 1,
                      limit: 20,
                      status: '',
                      category: '',
                      tier: '',
                      verified: '',
                      featured: '',
                      search: '',
                    });
                  }}
                  className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 border border-gray-100"
                >
                  Clear
                </button>
                <button
                  type="submit"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 py-1.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs font-medium"
                >
                  Apply
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Businesses Table */}
        <div className="bg-white rounded-xl p-3 shadow-neomorph border border-gray-100 mb-3">
          <BusinessTable
            businesses={marketplaceBusinesses || []}
            pagination={marketplaceBusinessesPagination}  // ← ADD THIS
            onView={handleViewBusiness}
            onEdit={handleEditBusiness}
            onVerify={handleVerifyBusiness}
            onRefresh={loadBusinesses}
            onPageChange={handlePageChange}              // ← ADD THIS
            isLoading={isLoading}
          />
        </div>

        {/* Pagination Controls */}
        {marketplaceBusinessesPagination && marketplaceBusinessesPagination.total > 0 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="flex items-center space-x-2">
              <label className="text-xs text-gray-600">Show:</label>
              <select
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-1.5 py-1 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs text-gray-500">per page</span>
            </div>

            <p className="text-xs text-gray-500 bg-white rounded-lg px-2.5 py-1 shadow-neomorph-inset">
              Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
              <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
              <span className="font-medium text-gray-800">{total}</span>
            </p>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="First"
              >
                <ChevronsLeft className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === 1 || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Previous"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>

              <div className="flex items-center space-x-0.5">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                          : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Next"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages || isLoading}
                className={`p-1.5 rounded-lg transition-all ${
                  currentPage === totalPages || isLoading
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                }`}
                title="Last"
              >
                <ChevronsRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Verify Business Modal */}
      {showVerifyModal && businessToVerify && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => setShowVerifyModal(false)}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-5 border border-primary-100/30">
              <div className="sm:flex sm:items-start">
                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-xl shadow-lg sm:mx-0 ${
                  verifyAction 
                    ? 'bg-gradient-to-br from-green-500 to-green-600 shadow-green-500/30' 
                    : 'bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-yellow-500/30'
                }`}>
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div className="mt-2 text-center sm:mt-0 sm:ml-3 sm:text-left">
                  <h3 className="text-base font-semibold text-gray-800">
                    {verifyAction ? 'Verify Business' : 'Remove Verification'}
                  </h3>
                  <div className="mt-1">
                    <p className="text-xs text-gray-500">
                      {verifyAction 
                        ? `Verify "${businessToVerify.name}"? A verification badge will appear.`
                        : `Remove verification from "${businessToVerify.name}"? The badge will disappear.`
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={confirmVerifyBusiness}
                  disabled={isLoading}
                  className={`w-full inline-flex justify-center rounded-lg border border-transparent px-3 py-1.5 text-xs font-medium text-white shadow-md hover:shadow-lg focus:outline-none sm:ml-3 sm:w-auto ${
                    verifyAction 
                      ? 'bg-gradient-to-r from-green-600 to-green-700 shadow-green-500/30 hover:shadow-green-500/40' 
                      : 'bg-gradient-to-r from-yellow-600 to-yellow-700 shadow-yellow-500/30 hover:shadow-yellow-500/40'
                  }`}
                >
                  {isLoading ? 'Processing...' : (verifyAction ? 'Verify' : 'Remove')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowVerifyModal(false);
                    setBusinessToVerify(null);
                  }}
                  className="mt-2 w-full inline-flex justify-center rounded-lg border border-gray-200 px-3 py-1.5 bg-white text-xs font-medium text-gray-700 shadow-neomorph hover:shadow-neomorph-hover focus:outline-none sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default AdminBusinessesPage;